import { withOktaAuth } from "@okta/okta-react";
import { AuthState } from "@okta/okta-auth-js";
import * as React from "react";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { responseListDefaultSort, Routes } from "../../AppConstants";
import { validateAuthState } from "../../Auth/AuthHelper";
import i18n from "../../i18n";
import { IStoreState } from "../../Model";
import { Error } from "../../Shared/Error";
import { LoadingIndicator } from "../../Shared/LoadingIndicator";
import { Responses } from "../../Shared/ResponseTable/ResponseList";
import { getResponseListThunk, setSortOrder, resetSortOrder } from "../duck/Actions";
import { WithOktaAuthProps } from "src/ts/Auth/duck/Models";

export interface IResponsesStateProps {
  failed: boolean
  fetched: boolean
  fetching: boolean
  responses: Api.IPagedResult<Api.IResponseListViewModel>
  orderBy: string
}

interface IResponsesPageDispatchProps {
  fetchResponseList: (
    pageNumber: number,
    itemPerPage: number,
    sortOrder: string,
    auth: AuthState | null,
    orderBy?: string
  ) => any;
  setSortOrder: (orderBy: string) => void;
  resetSortOrder: () => void;
}

interface IResponsesPageOwnProps {
  currentPage: number;
  sortOrder: string;
}

type IResponsesProps = IResponsesStateProps &
  IResponsesPageDispatchProps &
  IResponsesPageOwnProps &
  WithOktaAuthProps;

class ResponseListPage extends React.Component<IResponsesProps> {
  constructor(props: IResponsesProps) {
    super(props);

  }

  public componentDidMount() {
    if (this.props.authState?.isAuthenticated) {
      this.props.resetSortOrder();
      this.props.fetchResponseList(
        this.props.currentPage,
        this.props.responses.pageSize,
        `-${responseListDefaultSort}`,
        this.props.authState,
        this.props.orderBy
      );
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentDidUpdate(prevProps: IResponsesProps) {
    if (this.props.authState.isAuthenticated) {
      // Don't make another API call if one is already in progress
      // This prevents the race condition when page size changes trigger both
      // a direct API call from Pagination and a URL change that triggers componentDidUpdate
      if (this.props.fetching) {
        return;
      }
      if (
        prevProps.currentPage !== this.props.currentPage ||
        prevProps.orderBy !== this.props.orderBy
      ) {
        this.props.fetchResponseList(
          this.props.currentPage,
          this.props.responses.pageSize,
          this.props.sortOrder || `-${responseListDefaultSort}`,
          this.props.authState,
          this.props.orderBy
        );
      }
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentWillUnmount() {
    // Reset sorting when navigating away from the Updates tab
    this.props.resetSortOrder();
  }

  public handleSortChange = (orderBy: string) => {
    this.props.setSortOrder(orderBy);
  }

  public render() {
    if (!this.props.authState) {
      return <LoadingIndicator busy={true} />
    }
    if (this.props.authState?.error) {
      throw this.props.authState.error;
    }
    return (
      <React.Fragment>
        <h1 className="title">{i18n.t("responseList.heading")}</h1>
        {this.props.failed && (
          <Error message={i18n.t("responseList.fetchingResponsesFailed")} />
        )}
        <LoadingIndicator busy={this.props.fetching} />
        {this.props.fetched && (
          <Responses
            list={this.props.responses}
            baseRoute={Routes.responseList}
            isDraftList={false}
            handlePageSizeChange={this.props.fetchResponseList}
            authState={this.props.authState}
            onSortChange={this.handleSortChange}
            orderBy={this.props.orderBy}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IStoreState) => {
  return {
    ...state.responses
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchResponseList: (
      pageNumber: number,
      itemPerPage: number,
      sortOrder: string,
      auth: AuthState,
      orderBy?: string
    ) => {
      dispatch(
        getResponseListThunk(
          pageNumber,
          itemPerPage,
          sortOrder,
          auth,
          orderBy
        )
      );
    },
    setSortOrder: (orderBy: string) => {
      dispatch(setSortOrder(orderBy));
    },
    resetSortOrder: () => {
      dispatch(resetSortOrder());
    }
  };
};

const hoc = compose(
  withOktaAuth,
  connect<
    IResponsesStateProps,
    IResponsesPageDispatchProps,
    IResponsesPageOwnProps
  >(mapStateToProps, mapDispatchToProps)
)(ResponseListPage);

export { hoc as ResponseListPage };
